import { Dispatch, SetStateAction, useState } from 'react';

import { ETPayButton } from '../../../../components/button';
import { Modal } from '../../../../components/modal';
import { Spinner } from '../../../../components/spinner';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../hooks/useMerchant';
import { Beneficiary } from '../../../../models/payouts';
import { PayoutsService } from '../../../../services/payouts';
import { ErrorModalContent, ErrorModalDescription, ErrorModalTitle } from '../editModal/styles';
import OutboundResume from '../outboundResume';
import { Values } from '../table';
import { Button, ModalContainer, ModalFooter, ModalTitle, ResumeContainer } from './styles';

interface RemoveModalProps {
	beneficiary: Beneficiary;
	state: Values;
	setState: Dispatch<SetStateAction<Values>>;
	onClose: () => void;
}

interface TransferResumeItem {
	item: string;
	data: string;
}

const RemoveModal = ({ beneficiary, state, setState, onClose }: RemoveModalProps) => {
	const { merchantSelected } = useMerchant();
	const { handleShowAlert } = useFloatingAlert();
	const [countRetry, setCountRetry] = useState<number>(0);

	const addCountRetry = () => {
		setCountRetry((prevCount) => {
			const newCount = prevCount + 1;
			if (newCount < 3) {
				setState({
					...state,
					loading: false,
					error: true,
					errorMessage: 'Hubo un error al editar el beneficiario.',
					retryFunction: handleRemoveRequest,
				});
			} else {
				setState({
					...state,
					loading: false,
					error: true,
					errorMessage: 'Lo sentimos, superó la cantidad de reintentos.',
					retryFunction: onClose,
				});
			}
			return newCount;
		});
	};

	const resetCountRetry = () => {
		setCountRetry(0);
	};

	const handleRemoveRequest = async () => {
		setState({ ...state, loading: true });
		try {
			await PayoutsService.removeBeneficiary({
				merchantCode: merchantSelected?.code as string,
				rut: beneficiary.rut,
				merchant_id: beneficiary.merchant_id,
			});
			setState({ ...state, loading: false });
			handleShowAlert({
				message: 'Beneficiario removido con éxito',
				type: 'success',
			});
			resetCountRetry();
			onClose();
		} catch (error) {
			handleShowAlert({
				message: 'Hubo un problema al eliminar el beneficiario',
				type: 'error',
			});
			addCountRetry();
		}
	};

	const userResume: TransferResumeItem[] = [
		{
			item: 'RUT',
			data: beneficiary.rut,
		},
		{
			item: 'Nombre beneficiario',
			data: beneficiary.name,
		},
		{
			item: 'Correo beneficiario',
			data: beneficiary.email,
		},
	];

	const bankResume: TransferResumeItem[] = [
		{
			item: 'Banco',
			data: beneficiary.bankName,
		},
		{
			item: 'Número de cuenta',
			data: beneficiary.account,
		},
	];

	const renderContentModalByState = () => {
		if (state.loading) {
			return (
				<Modal
					show={true}
					type="medium"
					title="Procesando petición"
					allowCloseModal={false}
					onClose={onClose}
				>
					<Spinner />
				</Modal>
			);
		} else if (state.error) {
			return (
				<Modal show={true} type="medium" title="Error" onClose={onClose}>
					<ErrorModalContent>
						<ErrorModalTitle>Ha ocurrido un error</ErrorModalTitle>
						<ErrorModalDescription>
							{state.errorMessage ||
								'Lo sentimos, hubo un error con el proceso. Porfavor, inténtalo nuevamente'}
						</ErrorModalDescription>
						<ETPayButton
							title={countRetry < 3 ? 'Reintentar' : 'Cerrar'}
							color="secondary"
							onClick={state.retryFunction}
						/>
					</ErrorModalContent>
				</Modal>
			);
		} else {
			return (
				<Modal title="Remover beneficiario" show={true} type="medium" onClose={onClose}>
					<ModalContainer>
						<ModalTitle>Verifica la información a eliminar</ModalTitle>
						<ResumeContainer>
							<OutboundResume transferResume={userResume} />
							<OutboundResume transferResume={bankResume} />
						</ResumeContainer>
						<ModalFooter>
							<Button onClick={onClose}>Cancelar</Button>
							<Button onClick={handleRemoveRequest} removeColor>
								Eliminar
							</Button>
						</ModalFooter>
					</ModalContainer>
				</Modal>
			);
		}
	};

	return <>{renderContentModalByState()}</>;
};

export default RemoveModal;
